import { useDispatch, useSelector } from "react-redux"
import { useState, useRef, useEffect } from "react"
import {
  useFetchThreadChats,
  usePostThreadChat,
  useUploadMediaFile,
  useSendTemplate,
  useUpdateThreadRead,
} from "@/services/query/chats.query"
import { useAppointmentStatus } from "@/services/query/appointments.query"
import MessageBubble from "@/pages/chats/components/MessageBubble"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Textarea } from "@/components/ui/textarea"
import { DiamondPlus, Send, Loader2, UserCircle, ArrowLeft } from "lucide-react"
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useQueryClient } from "@tanstack/react-query"
import {
  setThreadDetails,
  setSelectedContact,
} from "@/services/store/slices/chats.slice"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { useInView } from "react-intersection-observer"
import { useToast } from "@/components/ui/use-toast"
import { Toaster } from "@/components/ui/toaster"
import { AppointmentStatsDialog } from "./components/AppointmentStatsDialog"
import {
  addTemporaryMessage,
  deleteLastMessage,
  replaceLastMessage,
  updateLastMessageStatus,
} from "./utils/message_helper"

function RightPanel() {
  const dispatch = useDispatch()
  const chats = useSelector((state) => state.chats)
  const [page, setPage] = useState(0)
  const limit = 100
  const [search, setSearch] = useState("")
  const [message, setMessage] = useState("")
  const [files, setFiles] = useState([])
  const fileInputRef = useRef(null)
  const messageContainerRef = useRef(null)
  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const { toast } = useToast()

  const {
    data: threadChats,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchThreadChats(chats?.selectedContact?.thread_id, 0, limit, search)

  const { mutate: sendMessage, isLoading: isSending } = usePostThreadChat()
  const { mutate: uploadFile, isLoading: isUploading } = useUploadMediaFile()
  const { mutate: sendTemplate, isLoading: isSendingTemplate } =
    useSendTemplate()
  const { mutate: markAsRead } = useUpdateThreadRead()

  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const [userScrolling, setUserScrolling] = useState(false)
  const scrollTimeout = useRef(null)
  const [isInfiniteScrolling, setIsInfiniteScrolling] = useState(false)

  const checkScrollPosition = () => {
    if (!messageContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } =
      messageContainerRef.current
    const scrollPosition = Math.abs(scrollHeight - scrollTop - clientHeight)
    setIsAtBottom(scrollPosition < 10)
  }

  const handleScroll = () => {
    const container = messageContainerRef.current
    if (!container) return

    setUserScrolling(true)

    const { scrollTop, scrollHeight, clientHeight } = container
    const scrollPosition = scrollTop + clientHeight
    const isNearTop = scrollTop < 100

    if (isNearTop && hasNextPage && !isFetchingNextPage) {
      setIsInfiniteScrolling(true)
      const oldScrollHeight = container.scrollHeight
      const oldScrollTop = container.scrollTop

      fetchNextPage()
        .then(() => {
          requestAnimationFrame(() => {
            if (messageContainerRef.current) {
              const newScrollHeight = messageContainerRef.current.scrollHeight
              const scrollOffset = newScrollHeight - oldScrollHeight
              messageContainerRef.current.scrollTop =
                scrollOffset + oldScrollTop
            }
          })
        })
        .finally(() => {
          setTimeout(() => {
            setIsInfiniteScrolling(false)
          }, 100)
        })
    }

    checkScrollPosition()

    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current)
    }
    scrollTimeout.current = setTimeout(() => {
      setUserScrolling(false)
    }, 150)
  }

  useEffect(() => {
    return () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current)
      }
    }
  }, [])

  useEffect(() => {
    const messageContainer = messageContainerRef.current
    if (messageContainer) {
      const scrollHandler = () => handleScroll()
      messageContainer.addEventListener("scroll", scrollHandler)
      return () => messageContainer.removeEventListener("scroll", scrollHandler)
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    if (threadChats?.messages && !isLoading && !isInfiniteScrolling) {
      messageContainerRef.current?.scrollTo({
        top: messageContainerRef.current.scrollHeight,
        behavior: isInitialLoad ? "instant" : "smooth",
      })
      setIsInitialLoad(false)
    }
  }, [threadChats?.messages, isLoading, isInfiniteScrolling, isInitialLoad])

  useEffect(() => {
    console.log(
      "#SDT ThreadId when Opening :",
      chats?.selectedContact?.thread_id
    )
    // TODO: Replace Hardcode values
    const searchQuery = ""
    const isRead = false
    const isRequested = false
    if (chats?.selectedContact?.thread_id && !chats?.selectedContact?.is_read) {
      markAsRead({
        threadId: chats.selectedContact.thread_id,
        search: searchQuery,
        isUnread: isRead,
        isRequested,
      })
    }
  }, [chats?.selectedContact?.thread_id])

  const handleSendMessage = () => {
    if (!message.trim() && files.length === 0) return

    // Create a more complete optimistic message
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      message: message.trim(),
      content: message.trim(), // Add content field for proper display
      created_at: new Date().toISOString(),
      modified_at: new Date().toISOString(),
      is_from_me: true,
      type: "STAFF", // Set proper type for user messages
      files: files.map((file) => URL.createObjectURL(file)),
      sending: true,
    }
    console.log("#SDT Optimistic Message", optimisticMessage)

    addTemporaryMessage(
      queryClient,
      chats?.selectedContact?.thread_id,
      optimisticMessage
    )

    const currentMessage = message.trim()
    const currentFiles = [...files]

    setMessage("")
    setFiles([])

    // Scroll to bottom immediately when sending a new message
    setTimeout(() => {
      if (messageContainerRef.current) {
        messageContainerRef.current.scrollTo({
          top: messageContainerRef.current.scrollHeight,
          behavior: "smooth",
        })
      }
    }, 100)

    sendMessage(
      {
        threadId: chats?.selectedContact?.thread_id,
        message: currentMessage,
        files: currentFiles,
      },
      {
        onSuccess: (data, variables) => {
          console.log("#SDT Message sent successfully:", data)
          //todo: extract required info from data and remove api call below

          const optimisticMessage2 = {
            id: data?.data?.id,
            message: data?.data?.content,
            content: data?.data?.content, // Add content field for proper display
            created_at: data?.data?.modified_at,
            modified_at: data?.data?.modified_at,
            is_from_me: true,
            type: "STAFF", // Set proper type for user messages
            files: files.map((file) => URL.createObjectURL(file)),
            sending: false,
          }
          replaceLastMessage(
            queryClient,
            variables.threadId,
            optimisticMessage2
          )
          // queryClient.invalidateQueries({
          //   queryKey: ["thread-chats", variables.threadId],
          //   refetchType: "all",
          // })
        },
        onError: (error) => {
          if (
            error?.details?.ticketStatus === "CLOSED" ||
            (error?.status === 400 && error?.details?.ticketStatus === "CLOSED")
          ) {
            toast({
              title: "Chat Expired",
              description:
                "Please click 'Reinitiate Chat' to start a new conversation.",
              variant: "destructive",
              duration: 5000,
            })
          }
          setMessage(currentMessage)
          setFiles(currentFiles)
          deleteLastMessage(queryClient, chats?.selectedContact?.thread_id)
          // queryClient.setQueryData(
          //   ["thread-chats", chats?.selectedContact?.thread_id],
          //   (old) => ({
          //     ...old,
          //     messages: old.messages.filter(
          //       (msg) => msg.chat_id !== optimisticMessage.chat_id
          //     ),
          //   })
          // )
        },
      }
    )
  }

  const handleFileSelect = (e) => {
    setFiles(Array.from(e.target.files))
  }

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Create optimistic message for file upload
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      content: URL.createObjectURL(file),
      message: URL.createObjectURL(file),
      type: "STAFF_FILE",
      modified_at: new Date().toISOString(),
      meta: {
        file_name: file.name,
        file_type: file.type,
      },
      created_at: new Date().toISOString(),
      is_from_me: true,
      sending: true,
    }

    addTemporaryMessage(
      queryClient,
      chats?.selectedContact?.thread_id,
      optimisticMessage
    )

    const currentMessage = message.trim()
    const currentFiles = [...files]
    setMessage("")
    setFiles([])

    // Scroll to bottom immediately when sending a new message
    setTimeout(() => {
      if (messageContainerRef.current) {
        messageContainerRef.current.scrollTo({
          top: messageContainerRef.current.scrollHeight,
          behavior: "smooth",
        })
      }
    }, 100)

    uploadFile(
      {
        threadId: chats?.selectedContact?.thread_id,
        mediaFile: file,
      },
      {
        onSuccess: (data, variables) => {
          console.log("#SDT WS File Upload:", data)
          console.log("#SDT WS meta:", data?.data?.meta)
          console.log(
            "#SDT WS metaStringyfy:",
            JSON.stringify(data?.data?.meta)
          )
          const optimisticMessage2 = {
            id: data?.data?.id,
            content: data?.data?.content,
            message: data?.data?.content,
            type: "STAFF_FILE",
            modified_at: data?.data?.modified_at,
            meta: data?.data?.meta,
            is_from_me: true,
            sending: false,
          }

          replaceLastMessage(
            queryClient,
            variables.threadId,
            optimisticMessage2
          )
          // queryClient.invalidateQueries({
          //   queryKey: ["thread-chats", variables.threadId],
          //   refetchType: "all",
          // })
        },
        onError: (error) => {
          if (
            error?.details?.ticketStatus === "CLOSED" ||
            (error?.status === 400 && error?.details?.ticketStatus === "CLOSED")
          ) {
            toast({
              title: "Chat Expired",
              description:
                "Please click 'Reinitiate Chat' to start a new conversation.",
              variant: "destructive",
              duration: 5000,
            })
          } else {
            toast({
              title: "Upload Failed",
              description: "Could not upload the file. Please try again.",
              variant: "destructive",
              duration: 3000,
            })
          }
          setMessage(currentMessage)
          setFiles(currentFiles)
          deleteLastMessage(queryClient, chats?.selectedContact?.thread_id)
        },
      }
    )
  }

  const handleReinitiate = () => {
    if (!chats?.selectedContact?.thread_id) return

    // Create optimistic message for reinitiate
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      content: "Chat reinitiated",
      created_at: new Date().toISOString(),
      modified_at: new Date().toISOString(),
      is_from_me: true,
      type: "STAFF",
      sending: true,
    }

    addTemporaryMessage(
      queryClient,
      chats?.selectedContact?.thread_id,
      optimisticMessage
    )

    const currentMessage = message.trim()
    const currentFiles = [...files]
    setMessage("")
    setFiles([])

    // Scroll to bottom immediately
    setTimeout(() => {
      if (messageContainerRef.current) {
        messageContainerRef.current.scrollTo({
          top: messageContainerRef.current.scrollHeight,
          behavior: "smooth",
        })
      }
    }, 100)

    sendTemplate(chats.selectedContact.thread_id, {
      onError: (error) => {
        setMessage(currentMessage)
        setFiles(currentFiles)
        deleteLastMessage(queryClient, chats?.selectedContact?.thread_id)
        console.error("Error sending template:", error)
        toast({
          title: "Reinitiate Failed",
          description: "Could not reinitiate chat. Please try again.",
          variant: "destructive",
          duration: 3000,
        })
      },
    })
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  /// For status update
  useEffect(() => {
    const { threadDetails } = chats
    if (!threadDetails) return

    const isAvailable = threadChats?.messages?.find(
      (chat) => chat.id === threadDetails.chat_id
    )

    console.log("#SDT Is Status Available for same chatId:", isAvailable)

    if (isAvailable) {
      // queryClient.invalidateQueries({
      //   queryKey: ["thread-chats", chats?.selectedContact?.thread_id],
      // })
      // const optimisticMessage = {
      //   chat_id: threadDetails?.chat_id,
      //   content: threadDetails?.message,
      //   created_at: new Date().toISOString(),
      //   modified_at: new Date().toISOString(),
      //   is_from_me: false,
      //   type: threadDetails?.status?.message_type,
      //   meta: threadDetails?.status,
      //   sending: false,
      // }
      updateLastMessageStatus(
        queryClient,
        chats.selectedContact.thread_id,
        threadDetails?.status
      )
    }

    dispatch(setThreadDetails(null))
  }, [
    chats.threadDetails,
    dispatch,
    threadChats?.messages,
    queryClient,
    chats?.selectedContact?.thread_id,
    chats,
  ])

  // For new message adding
  useEffect(() => {
    console.log("#SDT Current chats state:", chats)
    const { newThread } = chats
    if (!newThread) return
    console.log("#SDT New Thread:", newThread)

    console.log("#SDT Thread ID:", chats?.selectedContact?.thread_id)
    const isSame = chats?.selectedContact?.thread_id === newThread?.thread_id

    console.log("#SDT Is Same Thread", isSame)
    let optimisticMessage = null
    if (isSame) {
      console.log("#SDT New Thread message", newThread)
      if (newThread?.file_type && newThread?.file_path) {
        optimisticMessage = {
          id: newThread?.chat_id,
          content: newThread?.message,
          created_at: newThread?.thread_modified_at,
          modified_at: newThread?.thread_modified_at,
          is_from_me: false,
          meta: {
            file_name: newThread?.message,
            file_type: newThread?.file_type,
            file_path: newThread?.file_path,
          },
          type: newThread?.message_type,
          sending: false,
        }
      } else {
        optimisticMessage = {
          id: newThread?.chat_id,
          content: newThread?.message,
          created_at: newThread?.thread_modified_at,
          modified_at: newThread?.thread_modified_at,
          is_from_me: false,
          type: newThread?.message_type,
          sending: false,
        }
      }

      console.log("#SDT Thread Optimistic Message", optimisticMessage)

      addTemporaryMessage(
        queryClient,
        chats?.selectedContact?.thread_id,
        optimisticMessage
      )
    }

    dispatch(setThreadDetails(null))
  }, [chats.newThread, chats?.selectedContact?.thread_id])

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return ""

    const date = new Date(timestamp)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()

    if (isToday) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const messageTime = formatMessageTime(chats?.selectedContact?.modified_at)

  const { data: appointmentStatus } = useAppointmentStatus(
    chats?.selectedContact?.thread_id
  )

  const appointmentStats = appointmentStatus?.data?.data || {
    PENDING: 0,
    APPROVED: 0,
  }

  return (
    <div className="flex-1 flex flex-col h-full">
      <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        {chats?.selectedContact?.user_name ? (
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <button
                onClick={() => dispatch(setSelectedContact(null))}
                className="xl:hidden p-1 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <Avatar className="h-10 w-10 sm:h-12 sm:w-12">
                <AvatarImage src={chats?.selectedContact?.avatar} />
                <AvatarFallback>
                  {chats?.selectedContact?.user_name?.slice(0, 2)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold dark:text-gray-100 truncate">
                    {chats?.selectedContact?.user_name}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 hidden lg:inline-flex"
                    onClick={() =>
                      navigate(`/patients/${chats?.selectedContact?.thread_id}`)
                    }
                  >
                    See Patient Profile
                  </Button>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {chats?.selectedContact?.phone_number} • Last Updated:{" "}
                  {messageTime}
                </div>
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-4">
              <div className="flex flex-col items-center">
                <div className="text-amber-500 font-semibold text-lg">
                  {appointmentStats.PENDING}
                </div>
                <div className="text-xs text-gray-500">Pending</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-green-500 font-semibold text-lg">
                  {appointmentStats.APPROVED}
                </div>
                <div className="text-xs text-gray-500">Approved</div>
              </div>
              <AppointmentStatsDialog stats={appointmentStats} />
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 dark:text-gray-400 py-4">
            Select a contact to start chatting
          </div>
        )}
      </div>

      <div
        ref={messageContainerRef}
        className="flex-1 overflow-y-auto bg-gray-100 dark:bg-gray-900 p-4 scroll-smooth"
      >
        <div className="text-xs text-gray-500 mb-2">
          {hasNextPage ? "More messages available" : "No more messages"}
          {isFetchingNextPage && " (Loading...)"}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
          </div>
        ) : (
          <>
            {isFetchingNextPage && (
              <div className="flex justify-center py-2">
                <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
              </div>
            )}

            {[...(threadChats?.messages || [])].reverse().map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                patientId={chats?.selectedContact?.thread_id}
                messageId={message.id}
                sending={message.sending}
              />
            ))}
          </>
        )}
      </div>

      {chats?.selectedContact && (
        <div className="p-2 sm:p-3 flex items-end gap-2 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800">
          <Menubar className="p-0 m-0 h-[45px] shrink-0">
            <MenubarMenu>
              <MenubarTrigger>
                <DiamondPlus className="w-5 h-5" />
              </MenubarTrigger>
              <MenubarContent>
                <MenubarItem
                  disabled={isUploading}
                  onClick={() => {
                    const input = document.createElement("input")
                    input.type = "file"
                    input.accept = "image/*,video/*,application/*"
                    input.onchange = handleFileUpload
                    input.click()
                  }}
                >
                  {isUploading ? "Uploading..." : "Send File"}
                </MenubarItem>
                <MenubarItem
                  disabled={isSendingTemplate}
                  onClick={handleReinitiate}
                >
                  {isSendingTemplate ? "Sending..." : "Reinitiate Chat"}
                </MenubarItem>
              </MenubarContent>
            </MenubarMenu>
          </Menubar>

          <div className="flex-1">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="min-h-[45px] max-h-[120px] resize-none"
              disabled={isSending}
            />
          </div>

          <Button
            onClick={handleSendMessage}
            disabled={isSending || (!message.trim() && files.length === 0)}
            variant="ghost"
            size="icon"
            className="h-[45px] w-[45px] shrink-0"
          >
            {isSending ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
      )}

      {files.length > 0 && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Selected files: {files.length}
          </div>
        </div>
      )}
      <Toaster />
    </div>
  )
}

export default RightPanel
